import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Server, LayoutDashboard, LogOut, Flame, Menu, X, Users, Globe, AppWindow, Shield, Mail, Trash2, Bell, History, Terminal, Info, Gauge, ListOrdered } from 'lucide-react';
import { useState } from 'react';

export const Sidebar = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/incident-timeline', icon: ListOrdered, label: 'Incident-Timeline' },
    { path: '/customers', icon: Users, label: 'Customers' },
    { path: '/customer-overview', icon: Gauge, label: 'Kunden-Übersicht' },
    { path: '/domains', icon: Globe, label: 'Domains' },
    { path: '/certificates', icon: Shield, label: 'Certificates' },
    { path: '/mail', icon: Mail, label: 'Mail Health' },
    { path: '/cleanup', icon: Trash2, label: 'Cleanup' },
    { path: '/alerts', icon: Bell, label: 'Alerts' },
    { path: '/changes', icon: History, label: 'History' },
    { path: '/actions', icon: Terminal, label: 'Action Center' },
    { path: '/apps', icon: AppWindow, label: 'Fyre Apps' },
    { path: '/architecture', icon: Server, label: 'Server Architektur' },
    { path: '/mail-architecture', icon: Mail, label: 'Mail Architektur' },
  ];

  const sidebarContent = (
    <>
      {/* Brand */}
      <div className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
        <div className="w-10 h-10 rounded-lg bg-[#EF694C] flex items-center justify-center shadow-[0_0_15px_rgba(239,105,76,0.25)] overflow-hidden">
          <img src="/logo.webp" alt="FyreServe Logo" className="w-full h-full object-cover" />
        </div>
        <div>
          <h2 className="text-lg font-bold font-heading text-white tracking-tight">FyreServe</h2>
          <p className="text-xs text-[#52525b]">v1.0 MVP</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                active
                  ? 'bg-[#EF694C]/10 text-[#EF694C] border border-[#EF694C]/20'
                  : 'text-[#a1a1aa] hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-3 py-4 border-t border-white/5">
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-[#a1a1aa] hover:text-[#F43F5E] hover:bg-[#F43F5E]/5 transition-all duration-200"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-4 left-4 z-50 md:hidden w-10 h-10 rounded-lg bg-[#121214] border border-white/10 flex items-center justify-center text-white"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 z-40 h-screen w-64 bg-[#0c0c0e] border-r border-white/5 flex flex-col transition-transform duration-300 md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};
